import AutocompleteInput from "./AutocompleteInput";
import CompagniaField from "./CompagniaField";
import DateInput from "./DateInput";
import { VEICOLO_FIELDS } from "../lib/anagConfig";
import { isValidTarga } from "../lib/targa";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Plus, Trash2, Car } from "lucide-react";
import Combobox, { COLORI_VEICOLO } from "./Combobox";

const EMPTY = { targa: "", marca: "", modello: "", colore: "", anno_immatricolazione: "", numero_polizza: "", compagnia: "", scadenza_polizza: "", tipo_veicolo: "" };
const FIELD = Object.fromEntries(VEICOLO_FIELDS.map((f) => [f.key, f]));

export default function VeicoliEditor({ veicoli = [], onChange, hideHeader = false }) {
  const add = () => onChange([...(veicoli || []), { ...EMPTY }]);
  const remove = (i) => onChange(veicoli.filter((_, idx) => idx !== i));
  const set = (i, key, val) => onChange(veicoli.map((v, idx) => (idx === i ? { ...v, [key]: val } : v)));

  const renderField = (f, i, v) => f && (
    <div key={f.key}>
      <Label className="text-slate-400 text-xs">{f.label}</Label>
      {f.key === "compagnia" ? (
        <div className="mt-1">
          <CompagniaField value={v[f.key]} onChange={(val) => set(i, f.key, val)} testId={`veicolo-${i}-${f.key}`} />
        </div>
      ) : f.type === "autocomplete" ? (
        <div className="mt-1">
          <AutocompleteInput value={v[f.key]} onChange={(val) => set(i, f.key, val)} source={f.source}
            extraParams={f.source === "modelli" ? { marca: v.marca || "" } : {}} testId={`veicolo-${i}-${f.key}`} className="font-mono h-9" />
        </div>
      ) : f.type === "select" ? (
        <Select value={v[f.key] || undefined} onValueChange={(val) => set(i, f.key, val)}>
          <SelectTrigger data-testid={`veicolo-${i}-${f.key}`} className="mt-1 h-9 bg-slate-900/60 border-slate-700 text-slate-100 pgp-select-value"><SelectValue placeholder="Seleziona" /></SelectTrigger>
          <SelectContent className="bg-[#121824] border-slate-700 text-slate-100">
            {f.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
          </SelectContent>
        </Select>
      ) : f.key === "colore" ? (
        <div className="mt-1"><Combobox value={v.colore} onChange={(val) => set(i, "colore", val)} options={COLORI_VEICOLO} placeholder="Scegli o scrivi un colore" testId={`veicolo-${i}-colore`} /></div>
      ) : f.type === "date" ? (
        <div className="mt-1"><DateInput value={v[f.key]} onChange={(val) => set(i, f.key, val)} testId={`veicolo-${i}-${f.key}`} className="h-9 bg-slate-900/60 border-slate-700" /></div>
      ) : (
        <Input type="text" value={v[f.key] || ""} onChange={(e) => set(i, f.key, f.key === "targa" ? e.target.value.toUpperCase() : e.target.value)}
          data-testid={`veicolo-${i}-${f.key}`} className="mt-1 h-9 bg-slate-900/60 border-slate-700 font-mono" />
      )}
      {f.key === "targa" && v.targa && !isValidTarga(v.targa) && (
        <p className="mt-1 text-xs text-amber-600" data-testid={`veicolo-${i}-targa-error`}>Formato targa non riconosciuto — puoi comunque salvare.</p>
      )}
    </div>
  );

  return (
    <div data-testid="veicoli-editor">
      <div className="flex items-center justify-between mb-3">
        {!hideHeader ? (
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-200">
            <Car className="h-4 w-4 text-amber-400" /> Veicoli associati
          </div>
        ) : <span />}
        <Button type="button" size="sm" variant="outline" onClick={add} data-testid="add-veicolo" className="border-slate-700 text-slate-200 hover:bg-slate-800">
          <Plus className="h-4 w-4 mr-1" /> Aggiungi veicolo
        </Button>
      </div>

      {(!veicoli || veicoli.length === 0) ? (
        <div className="text-sm text-slate-500 border border-dashed border-slate-700 rounded-lg px-4 py-5 text-center">
          Nessun veicolo. Aggiungine uno.
        </div>
      ) : (
        <div className="space-y-4">
          {veicoli.map((v, i) => (
            <div key={i} className="rounded-lg border border-slate-800 bg-slate-900/40 p-4" data-testid={`veicolo-${i}`}>
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs uppercase tracking-wider text-slate-500 font-mono">Veicolo #{i + 1}</span>
                <button type="button" onClick={() => remove(i)} data-testid={`remove-veicolo-${i}`} className="text-red-400 hover:text-red-300">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                {renderField(FIELD.targa, i, v)}
                {renderField(FIELD.tipo_veicolo, i, v)}
              </div>
              <div className="grid sm:grid-cols-3 gap-3 mt-3">
                {renderField(FIELD.marca, i, v)}
                {renderField(FIELD.modello, i, v)}
                {renderField(FIELD.colore, i, v)}
              </div>
              <div className="grid sm:grid-cols-2 gap-3 mt-3">
                {renderField(FIELD.anno_immatricolazione, i, v)}
                {renderField(FIELD.numero_polizza, i, v)}
                {renderField(FIELD.compagnia, i, v)}
                {renderField(FIELD.scadenza_polizza, i, v)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
